import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { RedirectRequest } from '@azure/msal-browser';

import { User } from '../models/user';
import { environment } from '../../../environments/environment';
import { CommonService } from './common-service.service';
import { CookieService } from 'ngx-cookie-service';
import {
  decodeSessionFragment,
  isJwtExpired,
  scrubFragment,
  unwrap,
  WGINB_IDTOKEN_KEY,
  WGINB_USER_KEY
} from './sso-fragment.util';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private userInfo: User;
  homeContent: any;
  // private loginUrl = `${environment.BASE_URL}${environment.BASE_API.LOGIN}`;
  private loginUrl = `${environment.LOGIN_URL}`;
  private loginSsoUrl = `${environment.LOGIN_SSO_URL}`;
  private staticDataUrl  = `${environment.BASE_URL}${environment.BASE_API.STATIC}`;

  constructor(
    private http: HttpClient,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, 
    private msalService: MsalService,
    private commonService: CommonService,
    private cookieService: CookieService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string) {
    return this.http
      .post<any>(this.loginUrl, { username, password })
      .pipe(
        concatMap((user) => {
          this.userInfo = user;
          return this.http.get(this.staticDataUrl, {
            headers: { 'Authorization': this.userInfo.idtoken},
          });
        }),
        tap(staticDataResp => {
          this.userInfo.channels = staticDataResp['CHANNEL_LIST_DROPDOWN'];
          this.userInfo.HOME = staticDataResp['HOME'];
          this.userInfo.profiles = staticDataResp['PROFILES_LIST_DROPDOWN'];
          this.userInfo.CANDIDATE_LIST_SIZE=staticDataResp["SCREEN_RECORD_COUNT"]["VIEW_CANDIDATE_LIST_1"];
          this.userInfo.experience = staticDataResp['CANDIDATE_EXPERIENCE_DROPDOWN'];
          this.userInfo.fixedCtc = staticDataResp['CANDIDATE_FIXED_CTC_DROPDOWN'];
          this.userInfo.variableCtc = staticDataResp['CANDIDATE_VARIABLE_CTC_DROPDOWN'];
          this.userInfo.noticePeriod = staticDataResp['CANDIDATE_NOTICE_PERIOD_DROPDOWN'];
          this.userInfo.genderList = staticDataResp['GENDER_DROPDOWN'];
          this.userInfo.workMode = staticDataResp['WORK_MODE_DROPDOWN'];
          this.userInfo.TEXT_MAX_LENGTH = staticDataResp['TEXT_MAX_LENGTH'];
          // SECURITY NOTE: localStorage is vulnerable to XSS attacks. Consider moving sensitive tokens
          // to httpOnly cookies (server-side) or implementing a more secure storage mechanism.
          localStorage.setItem('currentUser', JSON.stringify(this.userInfo));
          localStorage.setItem('currentUserName', username);
          this.currentUserSubject.next(this.userInfo);
        })
      );
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.clear();
    sessionStorage.clear();
    this.currentUserSubject.next(null);
  }

  loginSso() {
    if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
        this.msalService.loginRedirect();
    }
}

  loginUsingSso(loginData) {
    return this.http
      .post<any>(this.loginSsoUrl, loginData)
      .pipe(
        concatMap((user) => this.enrichAndStore(user))
      );
  }

  /**
   * Given the login/ssologin response ({ idtoken, user, ... }), fetch the static data, attach it,
   * and persist `currentUser` to localStorage. Shared by the SSO login flow and the SSO-portal
   * fragment hydrator (which already has idtoken + user, so it skips MSAL/ssologin).
   */
  enrichAndStore(userInfo: any): Observable<any> {
    this.userInfo = userInfo;
    return this.http.get(this.staticDataUrl, {
      headers: { 'Authorization': this.userInfo.idtoken },
    }).pipe(
      tap(staticDataResp => {
        this.userInfo.channels = staticDataResp['CHANNEL_LIST_DROPDOWN'];
        this.userInfo.HOME = staticDataResp['HOME'];
        this.userInfo.profiles = staticDataResp['PROFILES_LIST_DROPDOWN'];
        this.userInfo.CANDIDATE_LIST_SIZE=staticDataResp["SCREEN_RECORD_COUNT"]["VIEW_CANDIDATE_LIST_1"];
        this.userInfo.experience = staticDataResp['CANDIDATE_EXPERIENCE_DROPDOWN'];
        this.userInfo.fixedCtc = staticDataResp['CANDIDATE_FIXED_CTC_DROPDOWN'];
        this.userInfo.variableCtc = staticDataResp['CANDIDATE_VARIABLE_CTC_DROPDOWN'];
        this.userInfo.noticePeriod = staticDataResp['CANDIDATE_NOTICE_PERIOD_DROPDOWN'];
        this.userInfo.genderList = staticDataResp['GENDER_DROPDOWN'];
        this.userInfo.workMode = staticDataResp['WORK_MODE_DROPDOWN'];
        this.userInfo.TEXT_MAX_LENGTH = staticDataResp['TEXT_MAX_LENGTH'];
        localStorage.setItem('currentUser', JSON.stringify(this.userInfo));
        localStorage.setItem('currentUserName', this.userInfo["user"]["USERNAME"]);
        this.commonService.setUserName(this.userInfo["user"]["USERNAME"]);
        this.currentUserSubject.next(this.userInfo);
      })
    );
  }

  /**
   * SSO Apps Portal handoff: if the URL carries a `#session=` fragment, hydrate the session
   * (token + user + static) directly from it so the user lands logged in without re-clicking SSO.
   * Runs as an APP_INITIALIZER, before the auth guard. Resolves false (and falls through to the
   * normal login screen) when there is no fragment, it is malformed, or the token is expired.
   */
  hydrateFromFragment(): Promise<boolean> {
    const session = decodeSessionFragment();
    if (!session) {
      return Promise.resolve(false);
    }

    const idtoken = unwrap(session[WGINB_IDTOKEN_KEY]);
    const user = unwrap(session[WGINB_USER_KEY]);

    if (typeof idtoken !== 'string' || !user || isJwtExpired(idtoken)) {
      scrubFragment();
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      this.enrichAndStore({ idtoken, user }).subscribe({
        next: () => {
          scrubFragment();
          resolve(true);
        },
        error: () => {
          scrubFragment();
          resolve(false);
        }
      });
    });
  }

  isMsalLoggedIn(): boolean {
    // FIX: Read MSAL tokens from sessionStorage instead of cookies
    // This prevents RequestHeaderSectionTooLarge error on hard refresh
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');

    if (msalAccessToken && msalIdToken) {
      try {
        const tokenData = JSON.parse(msalAccessToken);
        if (tokenData.expiry) {
          const expiryTime = new Date(tokenData.expiry).getTime();
          const now = new Date().getTime();
          return expiryTime > now;
        }
        return false;
      }
      catch {
        return false;
      }
    }
    return false;
  }
}
